import { User } from '../model/user';

/**
 * Safely reads the cached user from storage.
 *
 * Returns the parsed {@link User} when present, or an empty `User` when no user
 * is stored or the stored value is malformed — instead of throwing. This avoids
 * the `JSON.parse('')` -> `SyntaxError: Unexpected end of JSON input` crash that
 * occurred when components were constructed without a cached user (e.g. an
 * unauthenticated session). Mirrors the localStorage -> sessionStorage fallback
 * used for the auth token.
 */
export function getStoredUser(): User {
  const raw = localStorage.getItem('user') ?? sessionStorage.getItem('user');
  if (!raw) {
    return {} as User;
  }
  try {
    const parsed = JSON.parse(raw) as User & { data?: User };
    // Self-healing shim: forgot-password, reset-password, and create-account
    // used to write the raw API envelope `{ data: User }` instead of unwrapping
    // it first, so some browsers still have that shape cached. Left as-is, `id`
    // reads `undefined` and every guard treats the user as unauthenticated. Gate
    // on the *absence* of a usable top-level `id` so a plain `User` that happens
    // to carry its own `data` property is never mistaken for the wrapped shape.
    // Retire once these pre-fix cached values have aged out.
    if (!parsed?.id && parsed?.data && typeof parsed.data === 'object') {
      return parsed.data as User;
    }
    return parsed as User;
  } catch {
    return {} as User;
  }
}

/**
 * Writes the cached user back to storage.
 *
 * Persists to whichever storage currently holds the user (mirroring the
 * localStorage -> sessionStorage read order of {@link getStoredUser}), defaulting
 * to localStorage — which is where the login flow stores the authenticated user.
 * Storage failures (e.g. privacy mode) are swallowed; the in-memory state still
 * applies for the session.
 */
export function setStoredUser(user: User): void {
  const raw = JSON.stringify(user);
  try {
    if (localStorage.getItem('user') === null && sessionStorage.getItem('user') !== null) {
      sessionStorage.setItem('user', raw);
    } else {
      localStorage.setItem('user', raw);
    }
  } catch {
    // Storage unavailable — ignore.
  }
}

// The four session-token helpers below came from CONNECT 1.0, where the token
// sits in storage. Connect 2.0 keeps its session in an httpOnly cookie and must
// not write a token here; only the unregistered session-expired interceptor
// imports them. CON-158 decides whether they stay.

/**
 * The session token is written to both storages, as login always has. Readers
 * fall back from localStorage to sessionStorage.
 */
export function setStoredToken(token: string): void {
  localStorage.setItem('token', token);
  sessionStorage.setItem('token', token);
}

/** The stored session token (localStorage first, then sessionStorage), or '' when none. */
export function getStoredToken(): string {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
}

export function hasStoredSession(): boolean {
  return !!getStoredToken();
}

/** Removes the token and the user from both storages. */
export function clearStoredSession(): void {
  ['token', 'user'].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

/** The message login shows after the browser was signed out because the server refused its session. */
export const SESSION_ENDED_MESSAGE = 'You have been signed out. Please sign in again.';

const SESSION_ENDED_NOTICE_KEY = 'sessionEndedNotice';

/**
 * Leaves a one-shot note for the login page to show after a full page load,
 * which would lose a toast shown before it. It is not part of the stored
 * session, so {@link clearStoredSession} leaves it in place.
 */
export function setSessionEndedNotice(): void {
  sessionStorage.setItem(SESSION_ENDED_NOTICE_KEY, '1');
}

/** True once after {@link setSessionEndedNotice}: reading the note removes it. */
export function takeSessionEndedNotice(): boolean {
  const present = sessionStorage.getItem(SESSION_ENDED_NOTICE_KEY) !== null;
  sessionStorage.removeItem(SESSION_ENDED_NOTICE_KEY);
  return present;
}
